import type { ApplicationContext } from '@mimir/common/context/applicationContext';
import type { Logger } from '@mimir/common/types/logger';
import type { PremiereRestoreParameters } from './defaultColdStorageOps';
import { isInAdobeApp } from '../../../../common/src/context/applicationContext';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';
import { invariant } from '@mimir/common/util/invariant';
import { getGlobalStore } from '@mimir/web/store/index';
import { getUserDataPath } from '../desktop/utils';
import type { TenantConfigDto } from '@mimir/common/dto/tenantModel';
import type { ColdStorageRestoreOps, ColdStorageRestoreResponse } from '@mimir/client-common/coldStorage/coldStorageRestoreOps';
import type { RestoreTier } from '@mimir/common/dto/restoreMediaRequestDto';
import { DefaultNotificationOps } from '../premiere/defaultNotificationOps';
import { DefaultOngoingDownloadOps } from '../premiere/defaultOngoingDownloadOps';
import { DefaultDownloadAndRelinkOps } from '../premiere/defaultDownloadAndRelinkOps';


/**
 * This function prepares all the parameters needed to contruct
 * a DefaultColdStorageRestoreOps object.
 */
export function buildPremiereRestoreParameters(
  applicationContext: ApplicationContext,
  logger: Logger,
  localStateOps: LocalStateOps | undefined,
): PremiereRestoreParameters | undefined {
  if (!isInAdobeApp(applicationContext)) {
    return undefined;
  }

  invariant(localStateOps, 'localStateOps should be defined');
  const store = getGlobalStore();
  const ongoingUploads = store.getters.ongoingUploads;
  const isProxyMode = store.getters.isProxyWorkflowEnabled;
  const downloadPath = store.getters.downloadPath;
  const userDataSystemPath = getUserDataPath(applicationContext);
  const downloadToSubfolderPaths = (store.getters.featureFlags as TenantConfigDto['featureFlags'])?.keldaUsePrimaryRootSubfolders ?? false;
  const shouldUsePremierePreferences = store.getters.shouldUsePremierePreferences as boolean;
  const dummyColdStorageRestoreOps: ColdStorageRestoreOps = {
    requestRestore(_itemId: string, _restoreTier: RestoreTier, _retentionDays: number): Promise<ColdStorageRestoreResponse> {
      throw new Error('requestRestore() should not be invoked');
    }
  };
  const notificationOps = new DefaultNotificationOps(applicationContext);
  const ongoingDownloadOps = new DefaultOngoingDownloadOps();

  const skippedFileExtensions = (store.getters.tenantConfig as TenantConfigDto).skippedFileExtensionsForPremiere;
  const downloadAndRelinkOps = new DefaultDownloadAndRelinkOps(applicationContext, notificationOps, undefined, logger, ongoingDownloadOps, localStateOps, dummyColdStorageRestoreOps, downloadPath, userDataSystemPath, downloadToSubfolderPaths, shouldUsePremierePreferences, skippedFileExtensions);
  const premiereRestoreParameters: PremiereRestoreParameters = {
    services: {
      applicationContext: applicationContext,
      notificationOps,
      logger,
      ongoingDownloadOps,
      localStateOps,
      downloadAndRelinkOps,
    },
    config: {
      isProxyMode,
      isStandaloneMode: store.getters.selectedStorageProfile.isStandaloneMode,
      downloadPath,
      userDataSystemPath,
      shouldUsePremierePreferences,
      skippedFileExtensions,
    },
    ongoingUploads,
  };

  return premiereRestoreParameters;
}
